import { useMemo } from "react";
import type { PATMetricType } from "../types";
import { EventState } from "..";
import PATMetricGrid from "./PATMetricGrid";
import SectionHeading from "@/components/SectionHeading";

interface PATSetupSectionProps {
  onUpdate: (update: Partial<EventState>) => void;
  currentState: EventState;
  userCanEdit: boolean;
}

type MetricField = Exclude<
  keyof EventState["patEventAttributes"],
  "hostProfessional" | "examiner" | "conditions" | "problems"
>;

export function PATSetupSection({ onUpdate, currentState, userCanEdit }: PATSetupSectionProps) {
  const patMetrics: { [key: string]: PATMetricType[] } = useMemo(() => {
    const createMetric = (field: MetricField, label: string, editable = userCanEdit): PATMetricType => ({
      label,
      value: currentState.patEventAttributes[field]?.toString() || "",
      ...(editable && {
        onEdit: (newValue: string) => {
          let value: string | number = "";

          if (newValue.trim() === "") {
            value = "";
          } else if (field.includes("yardage")) {
            if (isNaN(parseInt(newValue, 10))) {
              value = "";
            } else {
              value = parseInt(newValue, 10);
            }
          } else if (field.includes("Rating")) {
            if (isNaN(parseFloat(newValue))) {
              value = "";
            } else {
              value = parseFloat(newValue);
            }
          } else {
            value = newValue;
          }

          onUpdate({
            patEventAttributes: {
              ...currentState.patEventAttributes,
              [field]: value
            }
          });
        }
      })
    });

    return {
      male: [
        createMetric("yardageMale", "Yardage - Male", userCanEdit),
        createMetric("courseRatingMale", "Course Rating - Male", userCanEdit),
        createMetric("yardageMaleFiftyPlus", "Yardage - Male 50+", userCanEdit),
        createMetric("targetScoreMale", "Target Score - Male", false)
      ],
      female: [
        createMetric("yardageFemale", "Yardage - Female", userCanEdit),
        createMetric("courseRatingFemale", "Course Rating - Female", userCanEdit),
        createMetric("yardageFemaleFiftyPlus", "Yardage - Female 50+", userCanEdit),
        createMetric("targetScoreFemale", "Target Score - Female", false)
      ]
    };
  }, [currentState, onUpdate, userCanEdit]);

  return (
    <>
      <SectionHeading heading="PAT Setup" />
      <div className="grid grid-cols-1 gap-3 w-full lg:grid-cols-2">
        <PATMetricGrid key="male" metrics={patMetrics.male} />
        <PATMetricGrid key="female" metrics={patMetrics.female} />
      </div>
    </>
  );
}
